<template>
  <div style="margin-top: 2rem">
    <!-- Header des thèmes -->
    <div class="themes-header mb-4">
      <div style="display: flex; align-items: center;">
        <h5 class="mb-0" style="font-weight: 600; color: #2c3e50;">
          {{ $t('editMultipleUserRights.themesManagement') }}
        </h5>
      </div>
      <v-btn
        class="add-theme-btn"
        color="#e4057d"
        style="color: white;"
        elevation="2"
        rounded
        @click.stop="dialog = true">
        <v-icon left>mdi-plus</v-icon>
        {{ $t('editMultipleUserRights.modifyThemesBtn') }}
      </v-btn>
    </div>

    <!-- Affichage des thèmes sélectionnés -->
    <div class="table-header-simple mb-3">
      <v-icon class="mr-2" style="margin-right: 8px;" color="#e4057d">mdi-view-list</v-icon>
      <span style="font-weight: 600; font-size: 1rem; color: #2c3e50;">
        {{ $t('editMultipleUserRights.selectedThemes') }}
      </span>
    </div>

    <v-treeview
      v-if="treeSelected.length > 0"
      :items="treeSelected"
      open-all
      item-text="title"
      item-value="id"
      :selectable="false"
      :activatable="false"
      hoverable
      class="selected-treeview"
    >
      <template v-slot:label="{ item }">
        <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
          <span style="font-weight: 500; color: #2c3e50;">{{ item.title }}</span>

          <!-- Bouton œil uniquement si c'est une feuille -->
          <v-tooltip bottom v-if="!item.children || item.children.length == 0">
            <template v-slot:activator="{ on, attrs }">
              <v-btn
                icon
                small
                color="#e4057d"
                class="action-btn"
                v-bind="attrs"
                v-on="on"
                @click.stop="showUsersWithAccess(item)"
              >
                <v-icon small>mdi-eye</v-icon>
              </v-btn>
            </template>
            <span>{{ $t('editMultipleUserRights.viewAssociatedRights') }}</span>
          </v-tooltip>
        </div>
      </template>
    </v-treeview>


    <!-- Message si aucun thème sélectionné -->
    <div v-else style="text-align: center; padding: 32px 24px;">
      <v-icon size="48" color="grey lighten-1">mdi-folder-open-outline</v-icon>
      <p style="font-size: 1.1rem; color: #666; margin: 12px 0 8px 0;">
        {{ $t('editMultipleUserRights.noThemesSelected') }}
      </p>
      <p style="color: #999; font-size: 0.9rem;">
        {{ $t('editMultipleUserRights.clickToAddThemes') }}
      </p>
    </div>


    <!-- Dialog des droits -->
    <v-dialog v-model="rightsDialog" max-width="500" persistent>
      <v-card class="rights-dialog">
        <v-card-title class="dialog-header primary white--text">
          <v-icon style="margin-right: 5px; margin-left: 10px;" color="white">mdi-shield-account</v-icon>
          <span>{{ $t('editMultipleUserRights.associatedRights') }} - {{ selectedThemeTitle  }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="rightsDialog = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-text class="dialog-content">
          <div v-if="currentRights.length > 0">
            <div class="rights-header mb-3">
              <v-icon color="success" class="mr-2">mdi-account-check</v-icon>
              <span style="font-weight: 600; color: #2e7d32;">{{ currentRights.length }} {{ $t('editMultipleUserRights.associatedCount') }}</span>
            </div>
            <v-list style="background: transparent">
              <v-list-item
                v-for="(right, index) in currentRights"
                :key="index"
                class="right-item">
                <v-list-item-avatar>
                  <v-icon color="success">mdi-check-circle</v-icon>
                </v-list-item-avatar>
                <v-list-item-content>
                  <v-list-item-title style="font-weight: 500; color: #2c3e50;">{{right}}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
            </v-list>
          </div>
          <div v-else style="text-align: center; padding: 32px 0;">
            <v-icon size="48" color="grey lighten-1">mdi-shield-off-outline</v-icon>
            <p style="color: #666; margin-top: 16px; font-weight: 500;">{{ $t('editMultipleUserRights.noAssociatedRights') }}</p>
            <p class="no-rights-subtitle">
              {{ $t('editMultipleUserRights.themeAssociatedSubtitle') }}
            </p>
          </div>
        </v-card-text>
      </v-card>
    </v-dialog>

    <v-dialog v-model="dialog" max-width="800" persistent>
      <v-card class="theme-selection-dialog">
        <v-card-title class="dialog-header primary white--text">
          <v-icon style="margin-right: 12px; margin-left: 10px" color="white">mdi-format-list-checks</v-icon>
          <span>{{ $t('editMultipleUserRights.selectedThemesTitle') }}</span>
          <v-spacer></v-spacer>
          <v-btn icon dark @click="annulerSelection">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-card-title>

        <v-card-subtitle style="color: #666; padding: 16px 24px 0 24px;">
          <div class="mt-2" style="display: flex; justify-content: center;">
            <v-chip color="#e4057d" style="color: white;" small>
              {{ themeSelectedInternal.length }} {{ $t('editMultipleUserRights.selectedCount') }}
            </v-chip>
          </div>
        </v-card-subtitle>

        <v-divider></v-divider>

        <v-card-text class="dialog-content">
          <v-treeview
            v-model="themeSelectedInternal"
            :items="themes"
            item-value="id"
            item-text="title"
            selectable
            return-object
            class="custom-treeview"
            color="#e4057d"
            selected-color="#e4057d">
            <template v-slot:label="{ item }">
              <div style="display: flex; align-items: center; justify-content: space-between; width: 100%;">
                <span>{{ item.title }}</span>
                <span v-if="!item.children || item.children.length == 0">
                  <span v-if="isAccessibleToAll(item)" style="font-size: 0.8rem; color: #888;">
                    Accessible à tous
                  </span>
                </span>
              </div>
            </template>
          </v-treeview>
        </v-card-text>

        <v-divider></v-divider>

        <v-card-actions style="padding: 16px 24px; background: #fafafa;">
          <v-btn
            text
            color="grey darken-1"
            @click="reinitialiser"
            class="cancel-btn">
            <v-icon left>mdi-cancel</v-icon>
            {{ $t('editMultipleUserRights.resetBtn') }}
          </v-btn>
          <v-spacer></v-spacer>
          <v-btn
            color="#e4057d"
            elevation="2"
            @click="valider"
            style="color: white;"
            class="validate-btn">
            <v-icon left>mdi-check</v-icon>
            {{ $t('editMultipleUserRights.validateBtn') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import Api from "@/core/services/api.service";
import userParentThemes from "@/core/utils/userParentThemes";

export default {
  name: "userRightMultipleThemeTable",
  model: { prop: "userRights", event: "input" },
  props: {
    userRights: { type: Array, default: () => [] },
  },
  data: () => ({
    dialog: false,
    themes: [],
    deletedThemes: [],
    addedThemes: [],
    themeSelectedInternal: [],
    rightsDialog: false,
    currentRights: [],
    selectedThemeForRights: null,
    originalThemesPerUserRight: [],
  }),

  computed: {
    themeSelected: {
      get() {
        return this.themeSelectedInternal;
      },
      set(val) {
        this.themeSelectedInternal = val;
      }
    },
    selectedThemeTitle() {
      return this.selectedThemeForRights ? this.selectedThemeForRights.title : "";
    },
    treeSelected() {
      var selectedIds = this.themeSelectedInternal.map(t => t.id);
      return this.filterTree(this.themes, selectedIds);
    }
  },

  watch: {
    userRights: {
      immediate: true,
      deep: true,
      handler() {
        if (!this.originalThemesPerUserRight.length && this.userRights.length) {
          this.originalThemesPerUserRight = this.userRights.map(ur => ur.themes ? [...ur.themes] : []);
        }
        this.deleteDoublonsThemes();
      },
    },

    themeSelectedInternal: {
      deep: true,
      handler(newVal, oldVal) {
        var newIds = newVal.map(t => t.id);
        var oldIds = oldVal.map(t => t.id);
        newVal.forEach(t => {
          if (!oldIds.includes(t.id) && !this.addedThemes.some(at => at.id == t.id)) {
            this.addedThemes.push(t);
          }
        });
        oldVal.forEach(t => {
          if (!newIds.includes(t.id) && !this.deletedThemes.some(dt => dt.id == t.id)) {
            this.deletedThemes.push(t);
          }
        });
        this.addedThemes = this.addedThemes.filter(t => newIds.includes(t.id));
        this.deletedThemes = this.deletedThemes.filter(t => !newIds.includes(t.id));
      }
    },
  },

  mounted() {
    Api.get("themes").then((res) => {
      this.themes = userParentThemes(res.data["hydra:member"]);
      this.deleteDoublonsThemes();
    });
  },
  
  methods: {
    filterTree(items, selectedIds) {
      if (!items || !Array.isArray(items)) return [];
      return items
        .map(item => {
          var children = this.filterTree(item.children, selectedIds);
          if (selectedIds.includes(item.id) || children.length > 0) {
            return { ...item, children };
          }
          return null;
        })
        .filter(Boolean);
    },

    isAccessibleToAll(item) {
      return this.userRights.length > 0 &&
        this.userRights.every(ur => ur.themes?.some(t => t.id == item.id));
    },

    reinitialiser() {
      var resetUserRights = this.userRights.map((ur, index) => ({
        ...ur,
        themes: this.originalThemesPerUserRight[index]
          ? [...this.originalThemesPerUserRight[index]]
          : []
      }));

      var allThemes = resetUserRights.flatMap(ur => ur.themes);
      var uniqueMap = new Map();
      allThemes.forEach(t => uniqueMap.set(t.id, t));
      this.themeSelectedInternal = Array.from(uniqueMap.values());

      this.addedThemes = [];
      this.deletedThemes = [];
      this.dialog = false;

      this.$emit("input", resetUserRights);
    },
    
    deleteDoublonsThemes() {
      var allThemes = this.userRights.flatMap((ur) => ur.themes);
      var uniqueMap = new Map();
      allThemes.forEach((t) => uniqueMap.set(t.id, t));
      var allThemesArray = Array.from(uniqueMap.values());

      allThemesArray.forEach((theme) => {
        if (!this.themeSelectedInternal.some((selected) => selected.id == theme.id)) {
          this.themeSelectedInternal.push(theme);
        }
      });
    },

    annulerSelection() {
      var allThemes = this.userRights.flatMap((ur) => ur.themes);
      var uniqueMap = new Map();
      allThemes.forEach((t) => uniqueMap.set(t.id, t));
      this.themeSelectedInternal = Array.from(uniqueMap.values());

      this.addedThemes = [];
      this.deletedThemes = [];
      this.dialog = false;
    },

    valider() {
      var updatedUserRights = this.userRights.map(ur => {
        let newThemes = [...ur.themes];

        this.addedThemes.forEach(theme => {
          if (!newThemes.some(t => t.id == theme.id)) newThemes.push(theme);
        });

        this.deletedThemes.forEach(theme => {
          newThemes = newThemes.filter(t => t.id != theme.id);
        });

        return { ...ur, themes: newThemes };
      });

      this.dialog = false;
      this.addedThemes = [];
      this.deletedThemes = [];
      this.$emit("input", updatedUserRights);
    },

    showUsersWithAccess(theme) {
      this.selectedThemeForRights = theme;
      var rightsWithThisTheme = this.userRights
        .filter(ur => ur.themes?.some(t => t.id == theme.id))
        .map(ur => ur.name);
      this.currentRights = rightsWithThisTheme;
      this.rightsDialog = true;
    },
  },
};
</script>


<style scoped>
/* Header des thèmes */
.themes-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.add-theme-btn {
  transition: all 0.3s ease;
}

.add-theme-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(228, 5, 125, 0.3);
}

/* Table header simple */
.table-header-simple {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding: 0 4px;
}

/* Boutons d'action */
.action-btn {
  transition: all 0.3s ease;
}

.action-btn:hover {
  transform: scale(1.1);
}

/* Dialog Styles */
.rights-dialog, .theme-selection-dialog {
  border-radius: 12px;
  overflow: hidden;
}

.dialog-header {
  display: flex;
  align-items: center;
  padding: 16px 24px;
}

.dialog-content {
  padding: 24px;
  max-height: 400px;
  overflow-y: auto;
}

/* Rights Header */
.rights-header {
  display: flex;
  align-items: center;
  padding: 12px;
  background: #e8f5e8;
  border-radius: 8px;
}

/* Rights List */
.right-item {
  border-radius: 8px;
  margin-bottom: 8px;
  background: #f8f9fa;
  transition: all 0.3s ease;
}

.right-item:hover {
  background: #e3f2fd;
  transform: translateX(4px);
}

.no-rights-subtitle {
  color: #888;
  font-size: 0.9rem;
  font-style: italic;
  margin-top: 8px;
}

/* Custom Treeview */
.custom-treeview {
  background: transparent;
}

.custom-treeview >>> .v-treeview-node__root {
  padding: 2px 5px;
  border-radius: 8px;
  margin: 4px 0;
  transition: all 0.3s ease;
}

.custom-treeview >>> .v-treeview-node__root:hover {
  background: #f0f4ff;
}

/* Dialog Actions */
.cancel-btn, .validate-btn {
  padding: 8px 24px;
  border-radius: 8px;
  font-weight: 500;
  transition: all 0.3s ease;
}

.validate-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 16px rgba(228, 5, 125, 0.3);
}
</style>